import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDolls, resolveImage } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { ArrowUpRight } from "lucide-react";

export default function Home() {
  const [dolls, setDolls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDolls()
      .then((data) => setDolls(data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen relative">
      <SiteHeader />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12 md:pb-20">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <p
              data-testid="hero-eyebrow"
              className="font-ui text-xs tracking-eerie uppercase text-[#e8aa42]/80 mb-6"
            >
              — Vicky's Private Collection
            </p>
            <h1
              data-testid="hero-title"
              className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-[#e2d6c8]"
            >
              Vicky's
              <br />
              <em className="not-italic text-[#e8aa42] font-normal">Lost Girls.</em>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 md:pl-8">
            <p className="font-body text-base md:text-lg text-[#a89f91] leading-relaxed border-l border-[#e8aa42]/30 pl-5">
              Each girl in Vicky's collection carries a story she cannot tell
              aloud. Approach one. Listen carefully. Some whispers were never
              meant for the living.
            </p>
          </div>
        </div>

        {/* A Safe Haven — Vicky's note */}
        <div className="mt-16 md:mt-20 grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-3">
            <p className="font-ui text-xs tracking-eerie uppercase text-[#e8aa42]/80">
              A Safe Haven
            </p>
            <p className="font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]/70 mt-2">
              — From Vicky, Their Keeper
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <p className="font-body text-lg md:text-xl text-[#cdbfb0] leading-relaxed max-w-3xl">
              The world was unkind to each of these girls. Some were forgotten,
              some were grieved, some were feared. Here, in Vicky's keeping,
              they will never be passed from stranger to stranger again. This
              cabinet is their home — a quiet sanctuary where their stories are
              told with care and their names are spoken with respect. They are
              safe here, for the rest of time.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
        <div className="flex items-baseline justify-between mb-10 border-b border-[#e8aa42]/15 pb-4">
          <h2 className="font-display text-2xl tracking-wide text-[#e2d6c8]">
            The Girls
          </h2>
          <span
            data-testid="gallery-count"
            className="font-ui text-xs tracking-eerie uppercase text-[#a89f91]"
          >
            {dolls.length || 4} Lost Girls
          </span>
        </div>

        {loading ? (
          <div
            data-testid="gallery-loading"
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10"
          >
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="aspect-[2/3] bg-[#1a1614] doll-arch animate-pulse border border-[#e8aa42]/10"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {dolls.map((doll, idx) => (
              <Link
                key={doll.id}
                to={`/dolls/${doll.id}`}
                data-testid={`doll-card-${doll.id}`}
                className="group block cursor-pointer"
                style={{ animationDelay: `${idx * 120}ms` }}
              >
                {/* Name above the portrait */}
                <div className="mb-4 text-center">
                  <p className="font-ui text-[10px] tracking-eerie uppercase text-[#e8aa42]/70 mb-1">
                    N° {String(doll.order).padStart(2, "0")} · Lost Girl
                  </p>
                  <h3
                    data-testid={`doll-name-${doll.id}`}
                    className="font-display text-3xl md:text-4xl text-[#e2d6c8] group-hover:text-[#e8aa42] transition-colors duration-500"
                  >
                    {doll.name}
                  </h3>
                </div>

                <div
                  data-testid={`doll-image-${doll.id}`}
                  className="relative overflow-hidden doll-arch border border-[#e8aa42]/20 bg-[#1a1614] shadow-[0_8px_32px_rgba(0,0,0,0.5)] aspect-[2/3] transition-all duration-700 group-hover:border-[#e8aa42]/60 group-hover:shadow-[0_12px_48px_rgba(139,0,0,0.35)]"
                >
                  <img
                    src={resolveImage(doll.image_url)}
                    alt={doll.name}
                    className="w-full h-full object-cover brightness-[0.78] saturate-[0.9] transition-all duration-700 group-hover:brightness-100 group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0f0c0b]/40 pointer-events-none" />
                </div>

                <div className="mt-4 flex items-center justify-between font-ui text-xs tracking-eerie uppercase text-[#a89f91] group-hover:text-[#e8aa42] transition-colors duration-500">
                  <span>Click to hear her story</span>
                  <ArrowUpRight
                    className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.25}
                  />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-[#e8aa42]/10 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-3 font-ui text-[10px] tracking-eerie uppercase text-[#a89f91]/70">
          <span>Vicky's Lost Girls · Curator's Archive</span>
          <span>Do not knock on the glass</span>
        </div>
      </footer>
    </div>
  );
}
